import { ArrowLeft, ArrowRight, Trash } from 'lucide-react';
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  addExperience,
  deleteExperience
} from '../../redux/Slices/ResumeSlice';
import Button from '../Common/Button';

type DropdownOption = { label: string; value: string };

type Props = {
  step: number;
  totalSteps: number;
  stepName: string;
  prevStepName: string;
  onNext: () => void;
  onBack: () => void;
  dropdowns?: {
    employmentTypes?: DropdownOption[];
    locationTypes?: DropdownOption[];
  };
};

const MAX_LIMITS: Record<string, number> = {
  jobTitle: 50,
  companyName: 50,
  location: 50,
  description: 200,
  achievements: 200,
  technicalSkillsUsed: 200
};

const RESPONSIBILITY_LIMIT = 100;

const ExperienceForm: React.FC<Props> = ({
  step,
  totalSteps,
  stepName,
  prevStepName,
  onNext,
  onBack,
  dropdowns
}) => {
  const dispatch = useDispatch();

  const experiences = useSelector((state: any) => state.root.resume.experience);

  const [form, setForm] = useState({
    jobTitle: '',
    companyName: '',
    employmentType: '',
    locationType: '',
    location: '',
    startDate: '',
    endDate: '',
    duration: 0,
    responsibilities: [{ key: crypto.randomUUID(), value: '' }],
    description: '',
    achievements: '',
    technicalSkillsUsed: '',
    currentRole: false
  });

  const [touched, setTouched] = useState<Record<string, boolean>>({});

  const handleChange = (e: any) => {
    const { name, value, type, checked } = e.target;
    const dateFields = ['startDate', 'endDate'];
    let formattedValue = type === 'checkbox' ? checked : value;

    if (MAX_LIMITS[name] && value.length > MAX_LIMITS[name]) return;

    if (dateFields.includes(name) && value) {
      formattedValue = new Date(value).toISOString();
    }

    setForm({
      ...form,
      [name]: formattedValue
    });
  };

  const handleResponsibilityChange = (value: string, key: string) => {
    if (value.length > RESPONSIBILITY_LIMIT) return;
    setForm(prev => ({
      ...prev,
      responsibilities: prev.responsibilities.map(r =>
        r.key === key ? { ...r, value } : r
      )
    }));
  };

  const addResponsibility = () => {
    setForm(prev => ({
      ...prev,
      responsibilities: [
        ...prev.responsibilities,
        { key: crypto.randomUUID(), value: '' }
      ]
    }));
  };

  const removeResponsibility = (key: string) => {
    setForm(prev => {
      const updated = prev.responsibilities.filter(r => r.key !== key);
      return {
        ...prev,
        responsibilities: updated.length
          ? updated
          : [{ key: crypto.randomUUID(), value: '' }]
      };
    });
  };

  const handleBlur = (e: any) => {
    setTouched(prev => ({ ...prev, [e.target.name]: true }));
  };

  const isSaveValid = form.companyName?.trim();

  const saveExperience = () => {
    if (!isSaveValid) return;

    dispatch(
      addExperience({
        ...form,
        responsibilities: form.responsibilities
          .map(r => r.value)
          .filter(v => v.trim()),
        duration:
          form.startDate && form.endDate
            ? new Date(form.endDate).getFullYear() -
              new Date(form.startDate).getFullYear()
            : 0
      })
    );

    setForm({
      jobTitle: '',
      companyName: '',
      employmentType: '',
      locationType: '',
      location: '',
      startDate: '',
      endDate: '',
      duration: 0,
      responsibilities: [{ key: crypto.randomUUID(), value: '' }],
      description: '',
      achievements: '',
      technicalSkillsUsed: '',
      currentRole: false
    });
    setTouched({});
  };

  const getExperienceKey = (exp: any) => `${exp.jobTitle}-${exp.companyName}`;

  const CharCount = ({
    value,
    max
  }: {
    value: string | undefined;
    max: number;
  }) => {
    const len = value?.length || 0;
    return (
      <span
        className={`text-xs ${len >= max ? 'text-red-500' : 'text-gray-400'}`}
      >
        {len}/{max}
      </span>
    );
  };

  return (
    <div className='w-full bg-gamma'>
      <div className='flex-1 p-10'>
        <div className='mb-4 flex justify-between'>
          <div>
            <p className='text-sm text-gray-500'>
              Step {step} of {totalSteps}
            </p>
            <h1 className='text-2xl font-semibold'>{stepName}</h1>
            <p className='mt-1 text-sm text-gray-400'>
              Add your work experience
            </p>
          </div>

          <div className='text-brand'>
            {Math.round(((step - 1) / totalSteps) * 100)} %
          </div>
        </div>

        <div className='mb-8 h-1 rounded bg-gray-200'>
          <div
            className='h-1 rounded bg-brand'
            style={{
              width: `${((step - 1) / totalSteps) * 100}%`
            }}
          />
        </div>

        {/* FORM */}
        <div className='rounded-xl bg-white p-6 shadow-sm'>
          <h3 className='mb-4 font-semibold'>+ Add Experience</h3>

          {/* job title + company */}
          <div className='grid grid-cols-2 gap-4'>
            <div>
              <div className='flex items-center justify-between'>
                <label className='text-sm font-medium'>Job Title</label>
                <CharCount value={form.jobTitle} max={50} />
              </div>
              <input
                name='jobTitle'
                value={form.jobTitle}
                onChange={handleChange}
                maxLength={50}
                placeholder='Enter role'
                className='mt-1 w-full rounded-lg border p-3 focus:border-gray-300 focus:outline-none'
              />
            </div>

            <div>
              <div className='flex items-center justify-between'>
                <label className='text-sm font-medium'>
                  Company Name <span className='text-red-500'>*</span>{' '}
                </label>
                <CharCount value={form.companyName} max={50} />
              </div>
              <input
                name='companyName'
                value={form.companyName}
                onChange={handleChange}
                onBlur={handleBlur}
                maxLength={50}
                placeholder='Enter company'
                className={`mt-1 w-full rounded-lg border p-3 focus:outline-none ${
                  touched.companyName && !form.companyName?.trim()
                    ? 'border-red-400 focus:border-red-400'
                    : 'focus:border-gray-300'
                }`}
              />
              {touched.companyName && !form.companyName?.trim() && (
                <p className='mt-1 text-xs text-red-500'>
                  Company name is required
                </p>
              )}
            </div>
          </div>

          {/* employment type + location type */}
          <div className='mt-4 grid grid-cols-2 gap-4'>
            <div>
              <label className='text-sm font-medium'>Employment Type</label>
              <select
                name='employmentType'
                value={form.employmentType}
                onChange={handleChange}
                className='mt-1 w-full rounded-lg border p-3 focus:border-gray-300 focus:outline-none'
              >
                <option value=''>Select type</option>
                {dropdowns?.employmentTypes?.map(opt => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className='text-sm font-medium'>Location Type</label>
              <select
                name='locationType'
                value={form.locationType}
                onChange={handleChange}
                className='mt-1 w-full rounded-lg border p-3 focus:border-gray-300 focus:outline-none'
              >
                <option value=''>Select type</option>
                {dropdowns?.locationTypes?.map(opt => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* location */}
          <div className='mt-4'>
            <div className='flex items-center justify-between'>
              <label className='text-sm font-medium'>Location</label>
              <CharCount value={form.location} max={50} />
            </div>
            <input
              name='location'
              value={form.location}
              onChange={handleChange}
              maxLength={50}
              placeholder='Enter location'
              className='mt-1 w-full rounded-lg border p-3 focus:border-gray-300 focus:outline-none'
            />
          </div>

          {/* dates + current role */}
          <div className='mt-4 grid grid-cols-3 gap-4'>
            <div>
              <label className='text-sm font-medium'>Start Date</label>
              <input
                type='date'
                name='startDate'
                value={form.startDate?.split('T')[0] || ''}
                onChange={handleChange}
                className='mt-1 w-full rounded-lg border p-3 focus:border-gray-300 focus:outline-none'
              />
            </div>

            <div>
              <label className='text-sm font-medium'>End Date</label>
              <input
                type='date'
                name='endDate'
                value={form.endDate?.split('T')[0] || ''}
                onChange={handleChange}
                min={form.startDate?.split('T')[0] || ''}
                disabled={form.currentRole}
                className={`mt-1 w-full rounded-lg border p-3 focus:border-gray-300 focus:outline-none
                  ${form.currentRole ? 'cursor-not-allowed bg-gray-100' : ''}`}
              />
            </div>

            <div className='flex items-end pb-3'>
              <label className='flex items-center text-sm'>
                <input
                  type='checkbox'
                  name='currentRole'
                  checked={form.currentRole}
                  onChange={handleChange}
                  className='mr-2'
                />
                Current Role
              </label>
            </div>
          </div>

          {/* responsibilities */}
          <div className='mt-4'>
            <label className='text-sm font-medium'>Responsibilities</label>
            {form.responsibilities.map(r => (
              <div key={r.key} className='mt-2 flex gap-2'>
                <div className='w-full'>
                  <div className='flex items-center gap-2'>
                    <input
                      value={r.value}
                      onChange={e =>
                        handleResponsibilityChange(e.target.value, r.key)
                      }
                      maxLength={RESPONSIBILITY_LIMIT}
                      placeholder='Enter responsibility'
                      className='w-full rounded-lg border p-3 focus:border-gray-300 focus:outline-none'
                    />
                    <span
                      className={`whitespace-nowrap text-xs ${
                        r.value.length >= RESPONSIBILITY_LIMIT
                          ? 'text-red-500'
                          : 'text-gray-400'
                      }`}
                    >
                      {r.value.length}/{RESPONSIBILITY_LIMIT}
                    </span>
                    {form.responsibilities.length > 1 && (
                      <button
                        onClick={() => removeResponsibility(r.key)}
                        className='whitespace-nowrap text-xs text-red-500'
                      >
                        <Trash size={16} />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
            <button
              onClick={addResponsibility}
              className='mt-2 text-sm text-brand'
            >
              + Add Responsibility
            </button>
          </div>

          {/* description */}
          <div className='mt-4'>
            <div className='flex items-center justify-between'>
              <label className='text-sm font-medium'>Description</label>
              <CharCount value={form.description} max={200} />
            </div>
            <textarea
              name='description'
              value={form.description}
              onChange={handleChange}
              maxLength={200}
              placeholder='Enter description'
              className='mt-1 w-full rounded-lg border p-3 focus:border-gray-300 focus:outline-none'
            />
          </div>

          {/* achievements */}
          <div className='mt-4'>
            <div className='flex items-center justify-between'>
              <label className='text-sm font-medium'>Achievements</label>
              <CharCount value={form.achievements} max={200} />
            </div>
            <textarea
              name='achievements'
              value={form.achievements}
              onChange={handleChange}
              maxLength={200}
              placeholder='Enter achievements'
              className='mt-1 w-full rounded-lg border p-3 focus:border-gray-300 focus:outline-none'
            />
          </div>

          {/* skills used */}
          <div className='mt-4'>
            <div className='flex items-center justify-between'>
              <label className='text-sm font-medium'>Skills Used</label>
              <CharCount value={form.technicalSkillsUsed} max={200} />
            </div>
            <input
              name='technicalSkillsUsed'
              value={form.technicalSkillsUsed}
              onChange={handleChange}
              maxLength={200}
              placeholder='Enter skills used'
              className='mt-1 w-full rounded-lg border p-3 focus:border-gray-300 focus:outline-none'
            />
          </div>

          <Button
            label='+ Save Experience'
            handleClickBtn={saveExperience}
            variant='contained'
            disabled={!isSaveValid}
            classes='mt-5'
          />
        </div>

        {/* ADDED EXPERIENCES */}
        <div className='mt-6 rounded-xl bg-white p-6 shadow-sm'>
          <h3 className='mb-4 font-semibold'>Added Experiences</h3>

          {experiences.length === 0 ? (
            <p className='text-sm text-gray-400'>No experiences added yet.</p>
          ) : (
            <div className='space-y-3'>
              {experiences.map((exp: any) => (
                <div
                  key={getExperienceKey(exp)}
                  className='flex items-center justify-between rounded-lg border p-3'
                >
                  <div>
                    <p className='font-medium'>{exp.jobTitle}</p>
                    <p className='text-xs text-gray-500'>
                      {exp.companyName} &bull; {exp.employmentType} &bull;{' '}
                      {exp.locationType}
                    </p>
                  </div>

                  <button
                    onClick={() =>
                      dispatch(deleteExperience(getExperienceKey(exp)))
                    }
                    className='text-xs text-red-500'
                  >
                    <Trash size={16} />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* footer */}
        <div className='mt-10 flex items-center justify-between'>
          <Button
            label={`Previous: ${prevStepName}`}
            handleClickBtn={onBack}
            variant='outlined'
          >
            <ArrowLeft size={16} />
          </Button>

          <Button
            label='Continue'
            handleClickBtn={onNext}
            variant='contained'
            iconPosition='right'
          >
            <ArrowRight size={16} />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ExperienceForm;
